/* eslint-disable import/no-default-export */
/* eslint-disable no-use-before-define */
import React, { useContext, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import dynamic from 'next/dynamic';
import serialize from 'serialize-javascript';
import classNames from 'classnames';
import { stub as $t } from '@nbcnews/analytics-framework';

import { adReady } from 'lib/adReady';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { useFeatureFlagContext } from 'lib/ContextTypes';
import { LayoutContext } from 'lib/ContextTypes/layout';
import { CURATOR_AD_PLACEHOLDER } from 'lib/brandFeatures';
import { stripQueryParams } from 'lib/urlUtils';
import AdSizes from 'lib/AdSizes.json';
import { VIEW } from 'lib/view';
import Breakpoints from 'lib/Breakpoints';
import './styles.themed.scss';

const FIRST_AD_TRACKING_EVENT = 'ramen_ads_first_queue_time';

$t?.('register', FIRST_AD_TRACKING_EVENT, { allowDuplicates: true });

// Dynamic import for Today vertical styles
const TodayAdStyles = dynamic(() => import('./Style/Today'), {
  ssr: false,
});

/**
 * Static class for tracking ad state across all instances
 */
export class AdState {
  /** @type {boolean} */
  static isFirstAdTracked = false;
}

/**
 * @typedef {Record<string, string|number|boolean|string[]|number[]>} AdTargeting
 */

/**
 * @typedef {object} MpsAdInstance
 * @property {(callback: () => void) => void} onRender
 */

/**
 * @typedef {(mpsAd: MpsAdInstance, mps: unknown) => void} AdHandler
 */

/**
 * @typedef {'topbanner'|'boxflex'|'boxinline'|'boxrail'|'midresponsive'|'sponsorlogo'|'interstitial'|'galleryslide'|'native'|'nativethreeup'|'nativeultradense'} AdSlotType
 */

/**
 * @typedef {'news'|'today'|'msnbc'|'telemundo'} VerticalType
 */

/**
 * @typedef {'front'|'cover'|'article'|'section'} PageViewType
 */

/**
 * @typedef {object} AdProps
 * @property {AdSlotType} slot
 * @property {AdTargeting} [targeting]
 * @property {boolean} [renderOnView]
 * @property {AdHandler} [handler]
 * @property {string} [adClass]
 * @property {boolean} [sticky]
 * @property {number} [offset]
 * @property {string} [packageId]
 * @property {number} [refreshInterval]
 * @property {boolean} [activeTab]
 * @property {string} [pkgClassName]
 * @property {boolean} [manuallyCuratedAd]
 * @property {number} [offsetViewport]
 * @property {string} [testId]
 * @property {boolean} [adsDisabled]
 * @property {PageViewType} [pageView]
 * @property {string} [pageRoute]
 * @property {string} [path]
 * @property {string} [view]
 */

/**
 * @param {AdProps} props
 * @returns {JSX.Element|null}
 */
function Ad(props) {
  const {
    slot,
    targeting = {},
    renderOnView = true,
    handler,
    adClass,
    sticky = false,
    offset,
    packageId,
    refreshInterval,
    activeTab = true,
    pkgClassName = 'ad',
    manuallyCuratedAd = false,
    offsetViewport,
    adsDisabled = false,
    pageView = 'article',
    pageRoute = '',
    path = '',
    view,
  } = props;

  const adContainerRef = useRef(/** @type {HTMLDivElement|null} */ (null));
  const featureFlags = useFeatureFlagContext();
  const layoutContext = useContext(LayoutContext);
  const vertical = /** @type {VerticalType | undefined} */ (layoutContext?.vertical);
  const isHomePage = stripQueryParams(path) === '/';

  const shouldShowAds = (() => {
    // Don't show if ads are globally disabled
    if (adsDisabled) return false;

    // disable all ads for Start Today app, except for sponsorships
    if (view === VIEW.START_TODAY_APP && slot !== 'sponsorlogo') {
      return false;
    }

    // Don't show non-curated ads when non-package ads are disabled
    if (layoutContext.nonPackageAdsDisabled && !manuallyCuratedAd) {
      return false;
    }

    return true;
  })();

  /**
   * @returns {string}
   */
  const getContainerClasses = () => {
    const verticalSpacing = Number(featureFlags['boxinline-ad-vertical-spacing']);
    const isPreviewMode = path && path.includes?.('__preview');
    const isPreviewEnabled = getFeatureConfigForBrand(CURATOR_AD_PLACEHOLDER, vertical);

    return classNames(
      pkgClassName,
      'dn-print',
      {
        'ad--text-alignment': ![
          'topbanner',
          'sponsorlogo',
          'interstitial',
          'midresponsive',
        ].includes(slot),
        'ad-container': !['topbanner', 'sponsorlogo', 'interstitial'].includes(slot),
        'ad--sticky': sticky,
        'ad--front': !!packageId,
        boxinlineAd: slot === 'boxinline',
        'boxinlineAd--50': slot === 'boxinline' && verticalSpacing === 50,
        'boxinlineAd--250': slot === 'boxinline' && verticalSpacing === 250,
        'ad--preview': isPreviewMode && isPreviewEnabled,
        bottomLineBoxLineAd: vertical === 'news' && isHomePage,
      },
      adClass,
    );
  };

  /** */
  function handleFirstAdTracking() {
    if (AdState.isFirstAdTracked) return;

    try {
      const queueData = window.adQueueTracker?.firstAdQueueTime?.();

      if (!queueData) {
        return;
      }

      /** @type {PageViewType} */
      const trackingPageView = pageView === 'front' && pageRoute === '/' ? 'cover' : pageView;
      const breakpoint = Breakpoints.isS() ? 'mobile' : 'desktop';

      $t('track', FIRST_AD_TRACKING_EVENT, {
        slot: queueData.slot,
        queueTime: Math.round(queueData.queueTime / 1000),
        pageView: trackingPageView,
        breakpoint,
      });

      AdState.isFirstAdTracked = true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Ad analytics tracking error:', error);
    }
  }

  /**
   * Sets up the MPS ad when adReady is triggered
   */
  useEffect(() => {
    if (!shouldShowAds) return;

    adReady((mps, createAd) => {
      const adContainer = adContainerRef.current;
      if (!adContainer) return;

      const mpsAd = createAd(adContainer);

      mpsAd.onRender(() => handleFirstAdTracking());

      handler?.(mpsAd, mps);
    });
  }, [shouldShowAds]);

  if (!shouldShowAds) {
    return null;
  }

  return (
    <>
      {vertical === 'today' && <TodayAdStyles />}
      <div className={getContainerClasses()} data-packageid={packageId} data-testid="ad__container">
        <div
          ref={adContainerRef}
          data-mps="true"
          data-slot={slot}
          data-sizes={serialize(AdSizes[slot] || [])}
          data-targeting={serialize(targeting)}
          data-render-on-view={String(!!renderOnView)}
          data-active-tab={String(!!activeTab)}
          data-offset-viewport={slot === 'boxinline' ? offsetViewport : undefined}
          data-offset={offset}
          data-refresh-interval={refreshInterval}
          data-testid="ad__container__inner"
        />
      </div>
    </>
  );
}

/**
 * Redux state structure for Ad component
 * @typedef {object} AdReduxState
 * @property {object} [front] - Front page state
 * @property {object} [front.curation] - Curation state
 * @property {boolean} [front.curation.adsDisabled] - Whether ads are disabled
 * @property {string} [front.curation.pageRoute] - Current page route
 * @property {object} [shared] - Shared state
 * @property {PageViewType} [shared.pageView] - Current page view type
 * @property {string} [shared.view] - Current view mode
 * @property {object} [router] - Router state
 * @property {string} [router.path] - Current path
 */

/**
 * Maps Redux state to component props with type safety
 * @param {AdReduxState} state - Redux state (unknown until validated)
 * @returns {object} Props object
 */
const mapStateToProps = (state) => ({
  adsDisabled: state?.front?.curation?.adsDisabled || false,
  pageView: state?.shared?.pageView || '',
  pageRoute: state?.front?.curation?.pageRoute || '',
  path: state?.router?.path || '',
  view: state?.shared?.view || null,
});

const ConnectedAd = connect(mapStateToProps)(Ad);

export default ConnectedAd;
